/**
 * BoundChat — the chat of a conversation bound to one thing the app shows: a page
 * (FEATURE-20261012-page-bound-chat-drawer-and-source-tab) or a checkpoint fileset
 * (FEATURE-20261023-checkpoint-fileset-chat-with-drafts).
 *
 * The binding is the server's: the conversation is created with it, so every turn is
 * briefed with the bound thing and the tools write only to it (godev pkg/aichat,
 * pkg/aitools). This component holds the conversation: the newest one in `folder`
 * is resumed on open, a first send creates one, a turn runs detached and its tool
 * rows and reply arrive through the conversation's change stream. The owner hears
 * when a turn starts and ends, and reloads what the turn may have changed.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Alert, Box, Button, CircularProgress, IconButton, MenuItem, Select, TextField, Typography } from "@mui/material";
import StopIcon from "@mui/icons-material/Stop";
import SendIcon from "@mui/icons-material/Send";
import { getLoomRpcClient } from "../../services/loom-rpc-client";
import type { AccountInfo, ChatMessage } from "../../pages/loom/types";
import { defaultModelFor } from "../../pages/loom/models";
import MarkdownContent from "../../pages/prompt/MarkdownContent";
import { onConversationChange } from "../../page-dsl/live";
import { toolRowSummary } from "./toolRowSummary";

const ACCOUNT_KEY = "page-chat.accountUid";

/** What a conversation is bound to. */
export type ChatBinding = { pageName: string } | { filesetUid: string };

export function BoundChat({
  open,
  folder,
  title,
  binding,
  placeholder,
  workingLabel,
  emptyHint,
  beforeTurn,
  onTurnStarted,
  onTurnEnded,
}: {
  open: boolean;
  /** the folder under /chats the bound conversations live in */
  folder: string;
  /** the title a new conversation gets */
  title: string;
  binding: ChatBinding;
  placeholder: string;
  workingLabel: string;
  emptyHint: ReactNode;
  /** runs before a turn starts; throwing stops the send and shows the error */
  beforeTurn?: () => Promise<void>;
  onTurnStarted?: () => void;
  /** the turn is over: its reply landed, or starting it failed */
  onTurnEnded: () => void;
}) {
  const [accounts, setAccounts] = useState<AccountInfo[]>([]);
  const [accountUid, setAccountUid] = useState("");
  const [conversationUid, setConversationUid] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  // The folder whose conversation has been loaded; loading until it is this one.
  const [loadedFor, setLoadedFor] = useState("");
  const loading = open && loadedFor !== folder;
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // On open: the accounts and the newest conversation in the folder.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const client = getLoomRpcClient();
      try {
        const acct = await client.listAccounts();
        const list = acct.accounts ?? [];
        if (cancelled) return;
        setAccounts(list);
        let remembered = "";
        try {
          remembered = localStorage.getItem(ACCOUNT_KEY) ?? "";
        } catch {
          // storage unavailable: the first account
        }
        setAccountUid(list.some((a) => a.uid === remembered) ? remembered : (list[0]?.uid ?? ""));

        const convs = await client.listConversations(folder);
        if (cancelled) return;
        const newest = [...(convs.conversations ?? [])].sort((a, b) => (a.lastActivity < b.lastActivity ? 1 : -1))[0];
        if (newest) {
          setConversationUid(newest.conversationUid);
          const msgs = await client.listMessages(newest.conversationUid);
          if (cancelled) return;
          setMessages(msgs.messages ?? []);
        } else {
          setConversationUid("");
          setMessages([]);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoadedFor(folder);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, folder]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const chooseAccount = (uid: string) => {
    setAccountUid(uid);
    try {
      localStorage.setItem(ACCOUNT_KEY, uid);
    } catch {
      // a convenience only
    }
  };

  // The conversation of the turn in flight.
  const inFlight = useRef<string | null>(null);

  // The turn's progress arrives through the conversation: each tool row and
  // the reply are rows of vfs_chat_message, so the change stream (with a slow
  // poll behind it) is what ends the wait, not the call that started the turn
  // (FEATURE-20261014-page-chat-hardening, stage 2).
  const refreshTurn = useCallback(async () => {
    const turn = inFlight.current;
    if (!turn) return;
    try {
      const refreshed = await getLoomRpcClient().listMessages(turn);
      const list = refreshed.messages ?? [];
      setMessages(list);
      const last = list[list.length - 1];
      if (last && last.role === "assistant" && inFlight.current === turn) {
        inFlight.current = null;
        setSending(false);
        onTurnEnded();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [onTurnEnded]);

  useEffect(() => {
    if (!sending || !conversationUid) return;
    const off = onConversationChange(conversationUid, () => void refreshTurn());
    const poll = setInterval(() => void refreshTurn(), 5000);
    return () => {
      off();
      clearInterval(poll);
    };
  }, [sending, conversationUid, refreshTurn]);

  const send = async () => {
    const said = text.trim();
    if (!said || sending) return;
    const client = getLoomRpcClient();
    setError(null);
    setSending(true);
    setText("");
    setMessages((prev) => [
      ...prev,
      { uid: `pending-${Date.now()}`, seq: prev.length + 1, role: "user", content: said, authorUid: "", model: "" },
    ]);
    onTurnStarted?.();
    try {
      if (beforeTurn) await beforeTurn();
      let uid = conversationUid;
      if (!uid) {
        const account = accounts.find((a) => a.uid === accountUid);
        if (!account) throw new Error("No LLM account is available to you; the chat cannot start.");
        const created = await client.createConversation({
          folder,
          title,
          llmAccountUid: account.uid,
          model: defaultModelFor(account.provider),
          ...binding,
        });
        if (created.errorMessage) throw new Error(created.errorMessage);
        uid = created.conversationUid;
        setConversationUid(uid);
      }
      inFlight.current = uid;
      const started = await client.startTurn(uid, said);
      if (started.errorMessage) throw new Error(started.errorMessage);
    } catch (e) {
      inFlight.current = null;
      setSending(false);
      setError(e instanceof Error ? e.message : String(e));
      onTurnEnded();
    }
  };

  const cancel = async () => {
    const turn = inFlight.current;
    if (!turn) return;
    try {
      const res = await getLoomRpcClient().cancelTurn(turn);
      if (res.errorMessage) setError(res.errorMessage);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const shown = messages.filter((m) => m.role === "user" || m.role === "assistant");
  // The tool rows of the turn in flight: everything after the last reply.
  const lastReply = messages.map((m) => m.role).lastIndexOf("assistant");
  const progress = sending ? messages.slice(lastReply + 1).filter((m) => m.role === "tool").map(toolRowSummary) : [];

  return (
    <>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 1 }}>
          {error}
        </Alert>
      )}
      <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {loading && <CircularProgress size={18} />}
        {!loading && shown.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {emptyHint}
          </Typography>
        )}
        {shown.map((m) => (
          <Box key={m.uid} sx={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", mb: 1.5 }}>
            <Box
              sx={{
                maxWidth: "88%",
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: m.role === "user" ? "primary.main" : "action.hover",
                color: m.role === "user" ? "primary.contrastText" : "text.primary",
              }}
            >
              {m.role === "user" ? (
                <Typography sx={{ whiteSpace: "pre-wrap" }}>{m.content}</Typography>
              ) : (
                <MarkdownContent content={m.content} />
              )}
            </Box>
          </Box>
        ))}
        {sending && (
          <Box sx={{ py: 1 }}>
            {progress.map((line, i) => (
              <Typography key={i} variant="caption" color="text.secondary" component="div" sx={{ fontFamily: "monospace" }}>
                {line}
              </Typography>
            ))}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                {workingLabel}
              </Typography>
              <Button size="small" startIcon={<StopIcon />} onClick={() => void cancel()} aria-label="cancel the turn">
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <Box sx={{ borderTop: 1, borderColor: "divider", p: 1 }}>
        {!conversationUid && accounts.length > 1 && (
          <Select size="small" value={accountUid} onChange={(e) => chooseAccount(String(e.target.value))} sx={{ mb: 1, minWidth: 220 }}>
            {accounts.map((a) => (
              <MenuItem key={a.uid} value={a.uid}>
                {a.displayName} ({a.provider})
              </MenuItem>
            ))}
          </Select>
        )}
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            size="small"
            placeholder={placeholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            disabled={sending || loading}
          />
          <IconButton color="primary" onClick={() => void send()} disabled={sending || loading || !text.trim()} aria-label="send">
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </>
  );
}
